import React from 'react';

import {
  Field,
  Grid,
  Select,
  SelectItem,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
  useQueryAdmins,
} from 'app/developer-v3/hooks';
import {
  INVITE_STATUSES,
  MEMBER_CONTACT_TYPES,
  ROLES,
} from 'app/developer-v3/constants';

import ContactDetailsHelp from './ContactDetailsHelp';
import Step from './Step';
import { ContactDetailsValues as Values } from './types';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

type Props = {
  initialValues: Values;
  integrationId: number;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function ContactDetails({
  initialValues,
  integrationId,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const { admins } = useQueryAdmins(String(integrationId));

  const acceptedAdmins = admins.filter(
    ({ role, status }) =>
      status === INVITE_STATUSES.accepted && role === ROLES.admin
  );

  const contactDropdownItems = acceptedAdmins.map(({ email }) => ({
    label: email,
    value: email,
  }));

  const marketingContactAdmin = acceptedAdmins.find(({ contactTypes }) =>
    contactTypes?.includes(MEMBER_CONTACT_TYPES.marketing)
  );
  const securityContactAdmin = acceptedAdmins.find(({ contactTypes }) =>
    contactTypes?.includes(MEMBER_CONTACT_TYPES.security)
  );
  const technicalContactAdmin = acceptedAdmins.find(({ contactTypes }) =>
    contactTypes?.includes(MEMBER_CONTACT_TYPES.technical)
  );
  const enforcedMarketingContactEmail =
    initialValues.marketingContactEmail === marketingContactAdmin?.email
      ? initialValues.marketingContactEmail
      : FIELD_BLANK_VALUES.marketingContactEmail;
  const enforcedSecurityContactEmail =
    initialValues.securityContactEmail === securityContactAdmin?.email
      ? initialValues.securityContactEmail
      : FIELD_BLANK_VALUES.securityContactEmail;
  const enforcedTechnicalContactEmail =
    initialValues.technicalContactEmail === technicalContactAdmin?.email
      ? initialValues.technicalContactEmail
      : FIELD_BLANK_VALUES.technicalContactEmail;
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      marketingContactEmail: enforcedMarketingContactEmail,
      securityContactEmail: enforcedSecurityContactEmail,
      technicalContactEmail: enforcedTechnicalContactEmail,
    },
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (
        values.marketingContactEmail ===
        FIELD_BLANK_VALUES.marketingContactEmail
      ) {
        errors.marketingContactEmail = 'Marketing contact email is required.';
      }
      if (
        values.securityContactEmail === FIELD_BLANK_VALUES.securityContactEmail
      ) {
        errors.securityContactEmail = 'Security contact email is required.';
      }
      if (
        values.technicalContactEmail ===
        FIELD_BLANK_VALUES.technicalContactEmail
      ) {
        errors.technicalContactEmail = 'Technical contact email is required.';
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);

  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="Contact details"
    >
      <ContactDetailsHelp integrationId={integrationId} />
      <Spacer height="20px" />
      <Grid columns="1" gap="20px">
        <Select
          errorMessage={getError(
            'marketingContactEmail',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled || contactDropdownItems.length === 0}
          isRequired={true}
          label="Marketing contact email"
          helpText="Who should we contact about co-marketing opportunities, launch announcements, and promotional features for your integration?"
          isInvalid={
            form.isTouched.marketingContactEmail &&
            !!form.errors.marketingContactEmail
          }
          onBlur={() => form.setFieldTouched('marketingContactEmail', true)}
          onChange={key =>
            form.setFieldValue('marketingContactEmail', key as string)
          }
          selectedKey={form.values.marketingContactEmail || null}
        >
          {contactDropdownItems.map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          errorMessage={getError(
            'securityContactEmail',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled || contactDropdownItems.length === 0}
          isRequired={true}
          label="Security contact email"
          helpText="Who should we contact about security incidents, vulnerability disclosures, or urgent security concerns affecting your integration?"
          isInvalid={
            form.isTouched.securityContactEmail &&
            !!form.errors.securityContactEmail
          }
          onBlur={() => form.setFieldTouched('securityContactEmail', true)}
          onChange={key =>
            form.setFieldValue('securityContactEmail', key as string)
          }
          selectedKey={form.values.securityContactEmail || null}
        >
          {contactDropdownItems.map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </Select>
        <Select
          errorMessage={getError(
            'technicalContactEmail',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled || contactDropdownItems.length === 0}
          isRequired={true}
          label="Technical contact email"
          helpText="Who should we contact about troubleshooting issues, technical questions, or integration reviews?"
          isInvalid={
            form.isTouched.technicalContactEmail &&
            !!form.errors.technicalContactEmail
          }
          onBlur={() => form.setFieldTouched('technicalContactEmail', true)}
          onChange={key =>
            form.setFieldValue('technicalContactEmail', key as string)
          }
          selectedKey={form.values.technicalContactEmail || null}
        >
          {contactDropdownItems.map(item => (
            <SelectItem key={item.value} value={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </Select>
        <Field
          error={getError('expectedResponseTime', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={false}
          label="Expected response time (hours)"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              If an issue is encountered within this integration after it's
              live, what's your expected response time?
            </Text>
          )}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-label="Expected response time (hours)"
              name="expectedResponseTime"
              onBlur={form.onBlur}
              onChange={form.onChange}
              type="number"
              value={form.values.expectedResponseTime}
            />
          )}
        />
      </Grid>
      <Spacer height="20px" />
      <AsyncButton
        aria-label="Save contact details"
        variant="secondary"
        isDisabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onPress={() => form.onSubmit()}
        type="button"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
